import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, IconButton, MenuItem,
  Paper, Stack, TextField, Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  approveMetaDraft, deleteMetaDraft, fetchMetaConfig, fetchMetaDrafts, fetchMetaDriveFiles,
  publishMetaDraft, suggestMetaDraft, updateMetaDraft,
  type MetaCampaignDraft, type MetaCampaignSpec, type MetaDriveFile,
} from '../../api/meta';
import { ApiError } from '../../api/client';
import { formatDateTime } from '../../components/formatters';

const STATUS_LABELS: Record<MetaCampaignDraft['status'], { label: string; color: 'default' | 'info' | 'success' | 'error' }> = {
  draft: { label: 'Szkic', color: 'default' },
  approved: { label: 'Zatwierdzona', color: 'info' },
  published: { label: 'Opublikowana (PAUSED)', color: 'success' },
  failed: { label: 'Błąd publikacji', color: 'error' },
};

const OBJECTIVES = [
  { value: 'OUTCOME_SALES', label: 'Sprzedaż' },
  { value: 'OUTCOME_TRAFFIC', label: 'Ruch' },
  { value: 'OUTCOME_AWARENESS', label: 'Zasięg' },
] as const;

const CTAS = ['SHOP_NOW', 'LEARN_MORE', 'SIGN_UP', 'GET_OFFER'];

function totalBudget(spec: MetaCampaignSpec): number {
  return spec.adsets.reduce((s, a) => s + (Number(a.dailyBudgetPln) || 0), 0);
}

function errMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Operacja nie powiodła się.';
}

function DraftEditor({ draft, presets, driveFiles, onDone }: {
  draft: MetaCampaignDraft;
  presets: Record<string, { label: string }>;
  driveFiles: MetaDriveFile[];
  onDone: () => void;
}) {
  const [spec, setSpec] = useState<MetaCampaignSpec>(draft.spec);
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['meta-drafts'] });
    onDone();
  };

  const save = useMutation({ mutationFn: () => updateMetaDraft(draft._id, spec), onSuccess: invalidate });
  const approve = useMutation({
    mutationFn: async () => {
      await updateMetaDraft(draft._id, spec);
      return approveMetaDraft(draft._id);
    },
    onSuccess: invalidate,
  });
  const publish = useMutation({ mutationFn: () => publishMetaDraft(draft._id), onSuccess: invalidate });

  const patchAdset = (i: number, patch: Partial<MetaCampaignSpec['adsets'][number]>) => {
    setSpec((s) => ({ ...s, adsets: s.adsets.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
  };
  const patchAd = (i: number, k: number, patch: Partial<MetaCampaignSpec['adsets'][number]['ads'][number]['creative']> & { name?: string }) => {
    setSpec((s) => ({
      ...s,
      adsets: s.adsets.map((a, j) =>
        j === i
          ? {
              ...a,
              ads: a.ads.map((ad, l) =>
                l === k
                  ? { name: patch.name ?? ad.name, creative: { ...ad.creative, ...patch, name: undefined } as typeof ad.creative }
                  : ad,
              ),
            }
          : a,
      ),
    }));
  };

  const busy = save.isPending || approve.isPending || publish.isPending;
  const editable = draft.status === 'draft' || draft.status === 'failed';

  return (
    <Box sx={{ pt: 1 }}>
      {draft.aiRationale && (
        <Alert severity="info" icon={<AutoAwesomeRoundedIcon fontSize="small" />} sx={{ mb: 2 }}>
          {draft.aiRationale}
        </Alert>
      )}
      {draft.error && <Alert severity="error" sx={{ mb: 2 }}>{draft.error}</Alert>}

      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            fullWidth size="small" label="Nazwa kampanii" value={spec.name} disabled={!editable}
            onChange={(e) => setSpec((s) => ({ ...s, name: e.target.value }))}
          />
          <TextField
            select size="small" label="Cel" value={spec.objective} disabled={!editable} sx={{ minWidth: 160 }}
            onChange={(e) => setSpec((s) => ({ ...s, objective: e.target.value as MetaCampaignSpec['objective'] }))}
          >
            {OBJECTIVES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Stack>

        {spec.adsets.map((adset, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
              <TextField fullWidth size="small" label="Nazwa adsetu" value={adset.name} disabled={!editable}
                onChange={(e) => patchAdset(i, { name: e.target.value })} />
              <TextField size="small" type="number" label="Budżet dzienny (PLN)" value={adset.dailyBudgetPln} disabled={!editable}
                sx={{ minWidth: 150 }} onChange={(e) => patchAdset(i, { dailyBudgetPln: Number(e.target.value) })} />
              <TextField select size="small" label="Targetowanie" value={adset.targetingPreset} disabled={!editable} sx={{ minWidth: 220 }}
                onChange={(e) => patchAdset(i, { targetingPreset: e.target.value })}>
                {Object.entries(presets).map(([key, p]) => <MenuItem key={key} value={key}>{p.label}</MenuItem>)}
              </TextField>
            </Stack>
            {adset.ads.map((ad, k) => (
              <Box key={k} sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider', mb: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField fullWidth size="small" label="Nazwa reklamy" value={ad.name} disabled={!editable}
                      onChange={(e) => patchAd(i, k, { name: e.target.value })} />
                    <TextField select size="small" label="Kreacja z Drive" disabled={!editable} sx={{ minWidth: 220 }}
                      value={ad.creative.driveFileId ?? ''}
                      onChange={(e) => patchAd(i, k, { driveFileId: e.target.value || undefined, existingCreativeId: undefined })}>
                      <MenuItem value="">— brak / istniejąca —</MenuItem>
                      {driveFiles.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                          {f.path ? `${f.path} / ` : ''}{f.name} ({f.kind}{f.imported ? ', zaimportowana' : ''})
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField size="small" label="ID istniejącej kreacji" disabled={!editable || !!ad.creative.driveFileId}
                      value={ad.creative.existingCreativeId ?? ''} sx={{ minWidth: 180 }}
                      onChange={(e) => patchAd(i, k, { existingCreativeId: e.target.value || undefined })} />
                  </Stack>
                  <TextField fullWidth size="small" label="Nagłówek" value={ad.creative.title} disabled={!editable}
                    onChange={(e) => patchAd(i, k, { title: e.target.value })} />
                  <TextField fullWidth multiline minRows={2} size="small" label="Tekst" value={ad.creative.body} disabled={!editable}
                    onChange={(e) => patchAd(i, k, { body: e.target.value })} />
                  <Stack direction="row" spacing={1}>
                    <TextField select size="small" label="CTA" value={ad.creative.cta} disabled={!editable} sx={{ minWidth: 140 }}
                      onChange={(e) => patchAd(i, k, { cta: e.target.value })}>
                      {CTAS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                    <TextField fullWidth size="small" label="Link" value={ad.creative.linkUrl} disabled={!editable}
                      onChange={(e) => patchAd(i, k, { linkUrl: e.target.value })} />
                  </Stack>
                  {!ad.creative.driveFileId && !ad.creative.existingCreativeId && (
                    <Typography variant="caption" color="warning.main">Wybierz kreację z Drive albo podaj ID istniejącej.</Typography>
                  )}
                </Stack>
              </Box>
            ))}
          </Paper>
        ))}

        {(save.isError || approve.isError || publish.isError) && (
          <Alert severity="error">{errMessage(save.error ?? approve.error ?? publish.error)}</Alert>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center">
          {editable && (
            <>
              <Button variant="outlined" disabled={busy} onClick={() => save.mutate()}>Zapisz szkic</Button>
              <Button variant="contained" disabled={busy} onClick={() => approve.mutate()}>
                Zatwierdź (łącznie {totalBudget(spec).toFixed(0)} PLN/dzień)
              </Button>
            </>
          )}
          {draft.status === 'approved' && (
            <Button variant="contained" color="warning" disabled={busy}
              startIcon={publish.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={() => publish.mutate()}>
              {publish.isPending ? 'Publikuję…' : 'Publikuj do Meta (jako PAUSED)'}
            </Button>
          )}
          {draft.status === 'published' && draft.publishedIds?.campaignId && (
            <Typography variant="body2" color="text.secondary">
              Kampania {draft.publishedIds.campaignId} czeka wstrzymana w Ads Managerze — włączysz ją ręcznie.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export function MetaCampaignsPanel() {
  const [brief, setBrief] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const draftsQuery = useQuery({ queryKey: ['meta-drafts'], queryFn: ({ signal }) => fetchMetaDrafts(signal) });
  const configQuery = useQuery({ queryKey: ['meta-config'], queryFn: ({ signal }) => fetchMetaConfig(signal) });
  const driveQuery = useQuery({
    queryKey: ['meta-drive-files'],
    queryFn: ({ signal }) => fetchMetaDriveFiles(signal),
    retry: false,
  });

  const suggest = useMutation({
    mutationFn: () => suggestMetaDraft(brief),
    onSuccess: (data) => {
      setBrief('');
      setExpanded(data.draft._id);
      void queryClient.invalidateQueries({ queryKey: ['meta-drafts'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteMetaDraft(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meta-drafts'] }),
  });

  const drafts = draftsQuery.data?.drafts ?? [];
  const driveFiles = driveQuery.data?.files ?? [];
  const presets = configQuery.data?.targetingPresets ?? {};

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6">Kreator kampanii</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        AI proponuje szkic → Ty przeglądasz i zatwierdzasz → publikacja do Meta zawsze jako PAUSED (uruchomienie ręcznie w Ads Managerze)
      </Typography>

      {driveQuery.isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Google Drive niedostępny: {errMessage(driveQuery.error)} — upewnij się, że folder kreacji jest udostępniony
          kontu serwisowemu i ustawiony w konfiguracji.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          fullWidth multiline minRows={2} size="small" label="Brief kampanii"
          placeholder="Np. Kampania sprzedażowa kolagenu premium na wrzesień, budżet 300 PLN/dzień, dwie grupy odbiorców"
          value={brief} onChange={(e) => setBrief(e.target.value)}
        />
        <Button
          variant="contained" sx={{ whiteSpace: 'nowrap', mt: { sm: 0.5 } }}
          startIcon={suggest.isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeRoundedIcon />}
          disabled={suggest.isPending || brief.trim().length < 10}
          onClick={() => suggest.mutate()}
        >
          {suggest.isPending ? 'Projektuję…' : 'Zaproponuj kampanię'}
        </Button>
      </Stack>
      {suggest.isError && <Alert severity="error" sx={{ mb: 2 }}>{errMessage(suggest.error)}</Alert>}

      <Divider sx={{ mb: 2 }} />

      {drafts.length === 0 && !draftsQuery.isLoading && (
        <Typography variant="body2" color="text.secondary">Brak szkiców kampanii — zacznij od briefu powyżej.</Typography>
      )}

      <Stack spacing={1.5}>
        {drafts.map((draft) => {
          const status = STATUS_LABELS[draft.status];
          const open = expanded === draft._id;
          return (
            <Paper key={draft._id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <IconButton size="small" onClick={() => setExpanded(open ? null : draft._id)}
                  sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
                  <ExpandMoreRoundedIcon />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>{draft.spec.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {draft.createdBy} · {formatDateTime(draft.createdAt)} · {draft.spec.adsets.length} adset(y) ·{' '}
                    {totalBudget(draft.spec).toFixed(0)} PLN/dzień
                  </Typography>
                </Box>
                <Chip size="small" color={status.color} label={status.label} />
                {draft.status !== 'published' && (
                  <IconButton size="small" onClick={() => remove.mutate(draft._id)} aria-label="Usuń szkic">
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              <Collapse in={open} unmountOnExit>
                <DraftEditor draft={draft} presets={presets} driveFiles={driveFiles} onDone={() => undefined} />
              </Collapse>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
}
